/*
 * Copyright (c) 2023. Tooling Team
 */

import PropTypes from "prop-types";
import { LoadingOverlay } from "@uhg-abyss/web/ui/LoadingOverlay";
import EIPMapAttributes from "./EIPMapAttributes";
import { FocusDock } from "./FocusDock";
import { MapTags } from "./MapTags";
import { EIPGrid } from "./table/EIPGrid";
import BottomBarButtons from "./table/BottomBarButtons";
import LabMapValueDefinitionGrid from "./LabMapValueDefinitionGrid";

export default function EIPMapValueDefinitionContainer(props) {
  const {
    mapFetching,
    tableLoading,
    suppressTableLoadingOverlay,
    mapStatus,
    mapVersion,
    mapVersionList,
    handleVersionChange,
    versionSelectResetKey,
    mapAttributes,
    editAttr,
    addAttr,
    applyTags,
    mapValueTags,
    allTags,
    mapTags,
    rowData,
    mapColumns,
    editRowsButtonDisabledHandler,
    bulkDeleteButtonDisabledHandler,
    onSelectedRowsChange,
    handleMultiEditFormData,
    isEditableVersion,
    addRow,
    editRows,
    cloneRows,
    bulkUpload,
    bulkDelete,
    handleBack,
    saveAsPublicDraft,
    saveButtonDisabled,
    handleUpdateMapStatus,
    publishButtonDisabled,
    submitForReviewDisabled,
    addButtonDisabled,
    editRowsButtonDisabled,
    bulkUploadButtonDisabled,
    bulkDeleteButtonDisabled,
    handleFocusChange,
    applySavedGridOptions,
    focusedRows,
    currentFocus,
    setCurrentFocus,
    isAllStandardCodeSystem,
    updatedRecords,
    isLabMap,
    labLoincValues,
    isOpenLabEditDialog,
    closeLabEditDialog,
    handleLabEditConfirm,
    initialLabTableState,
    onLabTableStateChange,
    onLabInlineEditingChange,
    getLabMvdReturnState,
  } = props;

  return (
    <>
      {mapFetching ? (
        <>
          <div className="row m-2 mb-4">
            <div className="col">
              <h3>Map Value Definition</h3>
              <div className="row">
                <div className="col mt-2">
                  <EIPMapAttributes
                    attributes={mapAttributes}
                    editAttr={editAttr}
                    addAttrOnClick={addAttr}
                    mapStatus={mapStatus}
                    mapVersion={mapVersion}
                    mapVersionList={mapVersionList}
                    handleVersionChange={handleVersionChange}
                    versionSelectResetKey={versionSelectResetKey}
                    disableVersion={isAllStandardCodeSystem}
                  />
                </div>
              </div>
            </div>
            <div className="col">
              <MapTags
                applyTags={applyTags}
                allTags={allTags}
                mapTags={mapTags}
                isCommonMap={
                  !!mapAttributes["Client"] &&
                  !!mapAttributes["Client"]["value"] &&
                  mapAttributes["Client"]["value"] === "All"
                }
              />
            </div>
          </div>
          <div className="row m-2 mb-4">
            {allTags.length !== 0 ? (
              <FocusDock
                mapValueTags={mapValueTags}
                handleFocusChange={handleFocusChange}
                currentFocus={currentFocus}
                setCurrentFocus={setCurrentFocus}
                applySavedGridOptions={applySavedGridOptions}
              />
            ) : (
              ""
            )}
            {tableLoading && !suppressTableLoadingOverlay ? (
              <LoadingOverlay
                isLoading={tableLoading}
                loadingTitle="Loading..."
                loadingMessage="Loading Map Value Definition..."
                ariaLoadingLabel="Loading Map Value Definition..."
              />
            ) : (
              <div className="col">
                {isLabMap ? (
                  <LabMapValueDefinitionGrid
                    rowData={rowData}
                    loincValues={labLoincValues}
                    isOpenEditDialog={isOpenLabEditDialog}
                    onCloseEditDialog={closeLabEditDialog}
                    onConfirmEdit={handleLabEditConfirm}
                    onOpenEditDialog={editRows}
                    onSelectedRowsChange={onSelectedRowsChange}
                    onEditRowsButtonDisabledChange={
                      editRowsButtonDisabledHandler
                    }
                    mapStatus={mapStatus}
                    isEditableVersion={isEditableVersion}
                    initialTableState={initialLabTableState}
                    onTableStateChange={onLabTableStateChange}
                    onInlineEditingChange={onLabInlineEditingChange}
                    getReturnState={getLabMvdReturnState}
                  />
                ) : (
                  <EIPGrid
                    mapStatus={mapStatus}
                    rowData={rowData}
                    mapColumns={mapColumns}
                    editRowsButtonDisabledHandler={
                      editRowsButtonDisabledHandler
                    }
                    bulkDeleteButtonDisabledHandler={
                      bulkDeleteButtonDisabledHandler
                    }
                    onSelectedRowsChange={onSelectedRowsChange}
                    onEditCellClicked={editRows}
                    onDeleteCellClicked={bulkDelete}
                    onSetStatus={handleMultiEditFormData}
                    isEditableVersion={isEditableVersion}
                    isAllStandardCodeSystem={isAllStandardCodeSystem}
                    applySavedGridOptions={applySavedGridOptions}
                    focusedRows={focusedRows}
                    currentFocus={currentFocus}
                    handleFocusChange={handleFocusChange}
                    updatedRecords={updatedRecords}
                  />
                )}
              </div>
            )}
          </div>
          <div className="row">
            <div className="col">
              <BottomBarButtons
                addRowOnClick={addRow}
                editRowsOnClick={editRows}
                cloneRowsOnClick={cloneRows}
                bulkUploadOnClick={bulkUpload}
                bulkDeleteOnClick={bulkDelete}
                backOnClick={handleBack}
                draftOnClick={saveAsPublicDraft}
                draftDisabled={saveButtonDisabled}
                handleUpdateMapStatus={handleUpdateMapStatus}
                publishDisabled={publishButtonDisabled}
                reviewDisabled={submitForReviewDisabled}
                addDisabled={addButtonDisabled}
                editRowsDisabled={editRowsButtonDisabled}
                bulkUploadDisabled={bulkUploadButtonDisabled}
                bulkDeleteDisabled={bulkDeleteButtonDisabled}
                disableAllActions={isAllStandardCodeSystem}
                hideAddRow={isLabMap}
                hideCloneRows={isLabMap}
                hideBulkDelete={isLabMap}
                hideBulkUpload={isLabMap}
              />
            </div>
          </div>
        </>
      ) : (
        ""
      )}
    </>
  );
}

EIPMapValueDefinitionContainer.propTypes = {
  mapFetching: PropTypes.bool,
  tableLoading: PropTypes.bool,
  suppressTableLoadingOverlay: PropTypes.bool,
  saveButtonDisabled: PropTypes.bool,
  publishButtonDisabled: PropTypes.bool,
  submitForReviewDisabled: PropTypes.bool,
  addButtonDisabled: PropTypes.bool,
  editRowsButtonDisabled: PropTypes.bool,
  bulkUploadButtonDisabled: PropTypes.bool,
  bulkDeleteButtonDisabled: PropTypes.bool,
  mapStatus: PropTypes.string,
  mapVersion: PropTypes.number,
  mapVersionList: PropTypes.array,
  handleVersionChange: PropTypes.func,
  versionSelectResetKey: PropTypes.number,
  mapAttributes: PropTypes.object,
  mapTags: PropTypes.array,
  applyTags: PropTypes.func,
  allTags: PropTypes.array,
  rowData: PropTypes.array,
  mapColumns: PropTypes.array,
  mapValueTags: PropTypes.array,
  editAttr: PropTypes.func,
  addAttr: PropTypes.func,
  concepSetHandler: PropTypes.func,
  editRowsButtonDisabledHandler: PropTypes.func,
  bulkDeleteButtonDisabledHandler: PropTypes.func,
  onSelectedRowsChange: PropTypes.func,
  editRow: PropTypes.func,
  deleteRow: PropTypes.func,
  handleMultiEditFormData: PropTypes.func,
  addRow: PropTypes.func,
  editRows: PropTypes.func,
  cloneRows: PropTypes.func,
  bulkUpload: PropTypes.func,
  bulkDelete: PropTypes.func,
  handleBack: PropTypes.func,
  saveAsPublicDraft: PropTypes.func,
  handleUpdateMapStatus: PropTypes.func,
  isEditableVersion: PropTypes.bool,
  handleFocusChange: PropTypes.func,
  applySavedGridOptions: PropTypes.bool,
  focusedRows: PropTypes.array,
  currentFocus: PropTypes.object,
  setCurrentFocus: PropTypes.func,
  isAllStandardCodeSystem: PropTypes.bool,
  updatedRecords: PropTypes.array,
  isLabMap: PropTypes.bool,
  labLoincValues: PropTypes.array,
  isOpenLabEditDialog: PropTypes.bool,
  closeLabEditDialog: PropTypes.func,
  handleLabEditConfirm: PropTypes.func,
  initialLabTableState: PropTypes.object,
  onLabTableStateChange: PropTypes.func,
  onLabInlineEditingChange: PropTypes.func,
  getLabMvdReturnState: PropTypes.func,
};
